import { ThemeProvider } from "@emotion/react";
import { FormApi, SubmissionErrors } from "final-form";
import { omit } from "lodash";
import { PureComponent, ReactNode } from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { Dispatch } from "redux";

import Redirect from "components/Redirect";
import FormMode from "constants/form-mode";
import { Right } from "constants/right";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { canDeleteAgent, canEditAgent, canManageAgentSignature, canManageAgentTeams } from "helpers/rights";
import { isExpired } from "helpers/subscription";
import { IStoreState } from "interfaces/storeState";
import { IKeyAny } from "services/api/api-client";
import { IAgent, getNonViewersCount, selectAgent, selectLoggedInAgent } from "store/entities/agents";
import { getBilling } from "store/entities/billing";
import { selectIsShopifyTrial, selectShopifyIntegration } from "store/entities/integrations";
import { selectManagedByPartner, selectManagedByPartnerEmail } from "store/entities/license";
import { selectMailboxesAlphabetically } from "store/entities/mailboxes/selectors";
import { selectCurrentSubscription } from "store/entities/subscriptions";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { selectCanAccessSettings } from "store/features/app";
import { SessionActions, hasRight } from "store/features/session";
import { AgentsViewActions } from "store/views/agents";
import { legacyTheme } from "themes/legacyTheme";

import { AgentsRoutes, RoleChangePermissions } from "../constants";

import { AgentForm, IAgentFormProps } from "./AgentForm";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: IStoreState, ownProps: RouteComponentProps) => ({
  currentAgent: selectLoggedInAgent(state),
  agent: selectAgent(state, (ownProps.match.params as IKeyAny).id),
  teams: selectTeamsAlphabetically(state),
  subscription: selectCurrentSubscription(state),
  billing: getBilling(state),
  nonViewersCount: getNonViewersCount(state),
  canManageAgents: hasRight(state, Right.ManageAgents),
  canManageOwnSignature: hasRight(state, Right.ManageOwnSignature),
  canAccessSettings: selectCanAccessSettings(state),
  mailboxes: selectMailboxesAlphabetically(state),
  shopifyIntegration: selectShopifyIntegration(state),
  isManagedByPartner: selectManagedByPartner(state),
  partnersContactEmail: selectManagedByPartnerEmail(state),
  isShopifyTrial: selectIsShopifyTrial(state),
});

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch: Dispatch) => ({
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onSubmit: (
    agent: Partial<IAgent>,
    _form: FormApi<IAgent, Partial<IAgent>>,
    _onError: (errors?: SubmissionErrors) => void,
    transactionID: string | null
  ) => {
    dispatch(
      // @ts-expect-error TS(2345)
      AgentsViewActions.updateAgent({
        id: agent.id,
        name: agent.name,
        teamIds: agent.teamIds,
        permission: agent.permission,
        signature: agent.signature,
        settings: omit(agent.settings, ["timeZone", "invertColorsInDarkMode", "canSendPrivateMsgAsViewer"]),
        autoassignment: agent.autoassignment,
        autoassignmentTeamIDs: agent.autoassignmentTeamIDs,
        ...(transactionID ? { transactionID } : {}),
      })
    );
  },
  // FIXME: Fix this the next time the file is edited.
  // @ts-expect-error TS(2345)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onDelete: (agentId: string) => dispatch(AgentsViewActions.deleteAgent({ agentId })),
  // FIXME: Fix this the next time the file is edited.
  // @ts-expect-error TS(2345)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onLogout: () => dispatch(SessionActions.logout()),
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
interface IProps extends RouteComponentProps, StateProps, DispatchProps {}

export class AgentFormContainer extends PureComponent<IProps> {
  constructor(props: IProps) {
    super(props);

    if (!props.agent) {
      this.props.history.push(AgentsRoutes.Agents);
    }
  }

  get roleChangePermissions(): RoleChangePermissions {
    if (!this.props.canManageAgents || this.props.agent.id === this.props.currentAgent.id) {
      return RoleChangePermissions.None;
    }

    if (isExpired(this.props.subscription)) {
      return RoleChangePermissions.CanChangeToViewerOnly;
    }

    return RoleChangePermissions.CanChange;
  }

  handleCancel = (): void => {
    this.props.history.push(AgentsRoutes.Agents);
  };

  handleSubmit = (
    values: Partial<IAgent>,
    form: FormApi<IAgent, Partial<IAgent>>,
    onSuccess: (errors?: SubmissionErrors) => void,
    transactionID: string | null
  ): void => {
    const changedFields = Object.entries(form.getState().dirtyFields)
      .filter(([_, value]) => value)
      .map(([key]) => key);
    trackEngagement("Agents: Agent updated", EventPlace.Agents, {
      changedFields,
      location: "Edit Agent",
      autoAssignmentState: values.autoassignment ? "On" : "Off",
    });
    this.props.onSubmit(values, form, onSuccess, transactionID);
  };

  handleGoToMailboxes = (): void => {
    this.props.history.push("/settings/email-addresses");
  };

  render(): ReactNode {
    const {
      agent,
      teams,
      subscription,
      billing,
      nonViewersCount,
      currentAgent,
      canManageAgents,
      canManageOwnSignature,
      canAccessSettings,
      mailboxes,
      onDelete,
      onLogout,
      shopifyIntegration,
      isManagedByPartner,
      partnersContactEmail,
      isShopifyTrial,
    } = this.props;

    if (!agent) {
      return null;
    }

    const props: IAgentFormProps = {
      agent,
      teams,
      subscription,
      // @ts-expect-error TS(2322)
      billing,
      nonViewersCount,
      mode: FormMode.Edit,
      isSelfManagement: agent === currentAgent,
      deleteAgentEnabled: canDeleteAgent(agent, currentAgent, canManageAgents),
      roleChangePermissions: this.roleChangePermissions,
      canManageAgentTeams: canManageAgentTeams(canManageAgents),
      canChangeSignature: canManageAgentSignature(agent.id, currentAgent.id, canManageAgents, canManageOwnSignature),
      canAccessSettings,
      mailboxes,
      onSubmit: this.handleSubmit,
      onCancel: this.handleCancel,
      onDelete,
      onLogout,
      shopifyIntegration,
      isManagedByPartner,
      partnersContactEmail,
      onGoToMailboxes: this.handleGoToMailboxes,
      isShopifyTrial,
    };

    const canEdit = canEditAgent(agent.id, currentAgent.id, canManageAgents);
    return (
      <>
        {!canEdit && <Redirect to="/agents" />}
        <ThemeProvider theme={legacyTheme}>
          <AgentForm key={agent.id} {...props} />
        </ThemeProvider>
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AgentFormContainer);
